import React from 'react';
import {
  Activity,
  AlertCircle,
  CalendarDays,
  ClipboardList,
  FileText,
  HeartPulse,
  Mail,
  MapPin,
  Pill,
  Phone,
  ShieldCheck,
  Stethoscope,
  User,
  X,
} from 'lucide-react';
import type { SmartClinicalItem, SmartPatientContextState, SmartResourceCount } from '../../types/smart';

interface SmartPatientInfoDrawerProps {
  open: boolean;
  onClose: () => void;
  asSidebar?: boolean;
  sidebarWidth?: number;
  context: SmartPatientContextState;
}

function formatDate(value?: string | null): string {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function patientLabel(value?: string | null): string {
  return value && value.trim() ? value.trim() : 'Not available';
}


function SectionTitle({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle?: string }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h3 className="truncate text-sm font-medium text-foreground">{title}</h3>
          {subtitle ? <p className="truncate text-[11px] text-text-tertiary">{subtitle}</p> : null}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  const text = value == null ? '' : String(value).trim();
  const has = text !== '' && text !== 'Not available';
  return (
    <div className="rounded-lg border border-border bg-background/70 px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-text-tertiary">{label}</div>
      <div className={`mt-1 wrap-break-word text-sm ${has ? 'font-medium text-foreground' : 'text-text-tertiary'}`}>
        {has ? text : '—'}
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, value }: { icon: React.ElementType; value: string }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-background/70 px-3 py-2 text-sm text-foreground">
      <Icon className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
      <span className="min-w-0 wrap-break-word">{value}</span>
    </div>
  );
}

function ClinicalList({
  title,
  icon,
  items,
  chartTotal,
}: {
  title: string;
  icon: React.ElementType;
  items: SmartClinicalItem[];
  /** How many the connected record holds, when it reported a count. */
  chartTotal?: number | null;
}) {
  if (!items.length) return null;
  // "12 shown" beside a resource card reading "40" stated two different sizes
  // for one list and reconciled neither. Say both numbers in one breath, and
  // only claim a total the record actually reported.
  const truncated = typeof chartTotal === 'number' && chartTotal > items.length;
  const subtitle = truncated
    ? `Showing ${items.length} of ${chartTotal} — open the full chart for the rest`
    : `${items.length} in the record`;
  return (
    <section id={sectionId(title)} className="scroll-mt-2 rounded-lg border border-border bg-surface-raised p-4 shadow-sm">
      <SectionTitle icon={icon} title={title} subtitle={subtitle} />
      <div className="space-y-2">
        {items.map((item) => (
          <div key={`${title}-${item.id}`} className="rounded-lg border border-border bg-background/70 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="line-clamp-2 text-sm font-medium text-foreground">{item.title}</div>
                {item.subtitle ? <div className="mt-1 line-clamp-1 text-[11px] text-text-tertiary">{item.subtitle}</div> : null}
              </div>
              {item.status ? (
                <span className="shrink-0 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                  {item.status}
                </span>
              ) : null}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-text-tertiary">
              {item.date ? <span>{formatDate(item.date)}</span> : null}
              {item.category ? <span>• {item.category}</span> : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function SmartPatientInfoDrawer({ open, onClose, asSidebar = false, sidebarWidth, context }: SmartPatientInfoDrawerProps) {
  if (!open) return null;

  const patient = context.patient;
  const preview = context.preview;
  const widthStyle = asSidebar && sidebarWidth ? { width: `${sidebarWidth}px` } : undefined;
  const containerClass = asSidebar
    ? 'relative z-20 h-full shrink-0 border-r border-border bg-background'
    : 'fixed inset-y-0 left-12 z-40 w-[min(94vw,28rem)] border-r border-border bg-background shadow-2xl';

  return (
    <aside className={containerClass} style={widthStyle} aria-label="Patient information">
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 border-b border-border px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-primary">
                <ShieldCheck className="h-3.5 w-3.5" />
                Connected record
              </div>
              <h2 className="mt-1 truncate text-base font-medium text-foreground">Patient information</h2>
              <p className="mt-1 text-xs text-text-tertiary">Read-only clinical details from the active patient record.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-surface-hover hover:text-foreground"
              aria-label="Close patient information"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <SmartPatientInfoSections context={context} />
        </div>
      </div>
    </aside>
  );
}


/**
 * The patient-record sections, without any panel chrome — the body of the
 * right-hand PatientSheet.
 *
 * Reported from the console as ugly and hard to use, and three things were
 * making it so:
 *
 *  1. THE SAME NUMBER, TWICE, DIFFERENT.  A "Clinical resources" grid showed
 *     big totals ("Conditions 40"), and further down the Conditions section
 *     said "12 shown". Neither mentioned the other, so the panel stated two
 *     different sizes for the same list and left the clinician to guess which
 *     was the chart. Sections now read "12 of 40" and say outright that the
 *     preview is capped, so the two numbers are one statement.
 *  2. ALLERGIES WERE FIFTH.  The order ran Conditions, Labs, Visits,
 *     Medications, THEN Allergies — the most safety-critical list on the panel
 *     sat below three that are often long. Ordered by what can hurt the
 *     patient now.
 *  3. NOWHERE TO JUMP.  Up to thirteen stacked cards in a 28rem column, pure
 *     scroll. The counts grid was already a table of contents; it just was not
 *     clickable. Now it is, and it doubles as the answer to "what is in here".
 *
 * The patient's name and MRN are deliberately NOT repeated here — the sheet
 * header states them and stays fixed above this body. They were previously
 * given three times (header, hero card, detail grid) in the first screenful.
 */

/** Which FHIR resource types back each section, so a section can report the
 *  chart's real total next to the number of rows it is showing. */
const SECTION_RESOURCE_TYPES: Record<string, string[]> = {
  Allergies: ['AllergyIntolerance'],
  Conditions: ['Condition'],
  Medications: ['MedicationRequest', 'MedicationStatement'],
  'Labs & vitals': ['Observation'],
  Visits: ['Encounter'],
  Procedures: ['Procedure'],
  'Reports & documents': ['DiagnosticReport', 'DocumentReference'],
  'Care plans & immunizations': ['CarePlan', 'Immunization'],
};

function sectionId(title: string): string {
  return `patient-section-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`;
}

/** The chart total for a section, or null when the connected record did not
 *  report one — in which case we say nothing rather than imply the list is
 *  complete. */
function chartTotal(title: string, counts: SmartResourceCount[] | undefined): number | null {
  const types = SECTION_RESOURCE_TYPES[title];
  if (!types || !counts?.length) return null;
  const matched = counts.filter((c) => types.includes(c.resourceType));
  if (!matched.length) return null;
  return matched.reduce((n, c) => n + c.count, 0);
}

export function SmartPatientInfoSections({ context }: { context: SmartPatientContextState }) {
  const patient = context.patient;
  const preview = context.preview;

  // Safety first, then what is being treated, then the evidence, then history.
  const sections: { title: string; icon: React.ElementType; items: SmartClinicalItem[] }[] = [
    { title: 'Allergies', icon: AlertCircle, items: preview?.allergies ?? [] },
    { title: 'Conditions', icon: HeartPulse, items: preview?.conditions ?? [] },
    { title: 'Medications', icon: Pill, items: preview?.medications ?? [] },
    { title: 'Labs & vitals', icon: Activity, items: preview?.observations ?? [] },
    { title: 'Visits', icon: CalendarDays, items: preview?.encounters ?? [] },
    { title: 'Procedures', icon: Stethoscope, items: preview?.procedures ?? [] },
    {
      title: 'Reports & documents',
      icon: FileText,
      items: [...(preview?.reports ?? []), ...(preview?.documents ?? [])].slice(0, 8),
    },
    {
      title: 'Care plans & immunizations',
      icon: ClipboardList,
      items: [...(preview?.carePlans ?? []), ...(preview?.immunizations ?? [])].slice(0, 8),
    },
  ];

  const present = sections.filter((s) => s.items.length > 0);

  return (
    <>
      {context.status === 'loading' && (
        <div className="rounded-lg border border-border bg-surface-raised p-4 text-sm text-text-tertiary">
          Loading patient details…
        </div>
      )}

      {context.status === 'error' && (
        <div className="rounded border border-destructive/20 bg-destructive-subtle p-4 text-sm text-destructive-text">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{context.error || 'Patient details are unavailable.'}</span>
          </div>
        </div>
      )}

      {patient && (
        <div className="space-y-4">
          {/* Demographics only. The name and MRN live in the sheet header,
              which stays fixed above this body. */}
          <section className="rounded-lg border border-border bg-surface-raised p-4 shadow-sm">
            <SectionTitle icon={User} title="Demographics" subtitle="From the connected record" />
            <div className="grid grid-cols-2 gap-2">
              <DetailRow label="Age" value={patient.ageYears != null ? `${patient.ageYears}` : 'Not available'} />
              <DetailRow label="Gender" value={patientLabel(patient.gender)} />
              <DetailRow label="Birth date" value={formatDate(patient.birthDate)} />
              <DetailRow label="Account" value={patientLabel(patient.accountNumber)} />
              <DetailRow label="Organization" value={patientLabel(patient.managingOrganization)} />
              <DetailRow
                label="Record"
                value={patient.active == null ? 'Not available' : patient.active ? 'Active' : 'Inactive'}
              />
            </div>
          </section>

          {/* The jump bar. It was already a list of everything in the panel --
              it just could not take you there. */}
          {present.length > 1 ? (
            <nav aria-label="Jump to a section" className="rounded-lg border border-border bg-surface-raised p-3 shadow-sm">
              <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.16em] text-text-tertiary">
                In this record
              </div>
              <div className="flex flex-wrap gap-1.5">
                {present.map((s) => {
                  const total = chartTotal(s.title, preview?.counts);
                  return (
                    <a
                      key={s.title}
                      href={`#${sectionId(s.title)}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/70 px-2.5 py-1 text-[11px] font-medium text-text-secondary transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {s.title}
                      <span className="tabular-nums text-text-tertiary">{total ?? s.items.length}</span>
                    </a>
                  );
                })}
              </div>
            </nav>
          ) : null}

          <section className="rounded-lg border border-border bg-surface-raised p-4 shadow-sm">
            <SectionTitle icon={MapPin} title="Contact & location" subtitle="Patient demographics" />
            <div className="space-y-2">
              <ContactRow icon={Phone} value={patient.phone} />
              <ContactRow icon={Mail} value={patient.email} />
              <ContactRow icon={MapPin} value={patient.address} />
              {!patient.phone && !patient.email && !patient.address ? (
                <div className="rounded-lg border border-border bg-background/70 p-3 text-sm text-text-tertiary">
                  No contact details were returned for this patient.
                </div>
              ) : null}
            </div>
          </section>

          {sections.map((s) => (
            <ClinicalList
              key={s.title}
              title={s.title}
              icon={s.icon}
              items={s.items}
              chartTotal={chartTotal(s.title, preview?.counts)}
            />
          ))}

          <section className="rounded-lg border border-border bg-surface-raised p-4 text-xs text-text-tertiary shadow-sm">
            <div className="font-medium uppercase tracking-[0.16em] text-text-tertiary">Source</div>
            <div className="mt-2 space-y-1">
              <div>Connection: {context.sessionId ? 'Active' : 'Reconnect to refresh'}</div>
              <div>Loaded: {formatDateTime(context.loadedAt)}</div>
              <div>Mode: read-only preview</div>
              {context.error ? <div className="text-band-moderate-text">Note: {context.error}</div> : null}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
